import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Download from "@insite/mobius/Icons/Download";
import Link2 from "@insite/mobius/Icons/Link2";
import Link from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import DocxLine from "@store/Components/Icons/DocxLine";
import HtmlLine from "@store/Components/Icons/HtmlLine";
import PdfLine from "@store/Components/Icons/PdfLine";
import XlsxLine from "@store/Components/Icons/XlsxLine";
import ZipLine from "@store/Components/Icons/ZipLine";
import { StoreFieldDefinition } from "@store/Framework/Types/FieldDefinition";
import GridItem, { GridItemProps } from "@store/MobiusOverrides/GridItem/GridItem";
import { gridGapStyles, linkStyles } from "@store/theme/common/mixins";
import { tableGridProps } from "@store/theme/common/props/tableGrid";
import { TYPOGRAPHY } from "@store/theme/design";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import React from "react";
import { FC } from "react";
import { css } from "styled-components";

export interface DocumentLinkModel {
    fields: {
        isLinkOrDocument: string;
        document: string;
        link: LinkFieldValue;
        overrideTitle: string;
        variant: string;
    }
}

const enum fields {
    documentsLinks = "documentsLinks",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.documentsLinks]: DocumentLinkModel[];
    };
}

export interface DocumentGridStyles {
    container?: InjectableCss;
    tableGridHead?: GridContainerProps;
    emptyHeaderGridItem?: GridItemProps;
    tableGridBody?: GridContainerProps;
    tableGridRow?: GridItemProps;
    tableGridRowContainer?: GridContainerProps;
    documentLinkTableGridCell?: GridItemProps;
    documentIconTableGridCell?: GridItemProps;
    titleLink?: IconProps;
    iconLink?: IconProps;
}

export const documentGridStyles: DocumentGridStyles = {
    container: tableGridProps.container,
    tableGridHead: tableGridProps.tableGridHead,
    emptyHeaderGridItem: {
        ...tableGridProps.tableGridHeader,
        width: 12,
        css: css`
            ${tableGridProps.tableGridHeader?.css}
            min-height: 46px;
        `,
    },
    tableGridBody: tableGridProps.tableGridBody,
    tableGridRow: tableGridProps.tableGridRow,
    tableGridRowContainer: {
        ...tableGridProps.tableGridRowContainer,
        css: css`
            ${tableGridProps.tableGridRowContainer?.css}
            ${gridGapStyles(
                ["24px", null, null, "40px"],
                ["10px", null, null, "40px"]
            )}

            ${GridOffset} {
                margin: 0;
            }
        `,
    },
    documentLinkTableGridCell: {
        ...tableGridProps.tableGridCell,
        width: [10, 10, 10, 9, 8],
        css: css`
            ${tableGridProps.tableGridCell?.css}
            align-items: center;
        `,
    },
    documentIconTableGridCell: {
        ...tableGridProps.tableGridCell,
        width: [2, 2, 2, 3, 4],
        css: css`
            ${tableGridProps.tableGridCell?.css}
            align-items: center;
            justify-content: center;
        `,
    },
    titleLink: {
        css: css`
            ${linkStyles()}
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.small};
            min-height: 3em;
            width: 100%;

            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, null, TYPOGRAPHY.size.base, TYPOGRAPHY.size.large], properties: ["font-size"] }],
                    mediaOptions.min,
                )}
        `,
    },
    iconLink: {
        css: css`
            min-height: 64px;
        `,
    },
};

const styles = documentGridStyles;

export const DocumentGrid: FC<OwnProps> = ({ fields }) => {
    return (
        <StyledWrapper {...styles.container}>
            {fields.documentsLinks.length > 0 && (
                <GridContainer {...styles.tableGridHead}>
                    <GridItem {...styles.emptyHeaderGridItem}/>
                </GridContainer>
            )}
            <GridContainer {...styles.tableGridBody}>
                {fields.documentsLinks.map(({ fields: { isLinkOrDocument, document, link, overrideTitle, variant } }, index) => {
                    const linkField = useGetLink(link);
                    const documentLinkHref = isLinkOrDocument === "Document" ? document : linkField.url;
                    const documentLinkTitle = isLinkOrDocument === "Document" ? document.split("/").pop() : linkField.title;
                    const getIconAndColor = (variant: string) => {
                        switch (variant) {
                            case "pdfDocument":
                                return { iconSrc: PdfLine, iconColor: "danger", iconSize: 64 };
                            case "docxDocument":
                                return { iconSrc: DocxLine, iconColor: "primary", iconSize: 64 };
                            case "xlsxSpreadsheet":
                                return { iconSrc: XlsxLine, iconColor: "success", iconSize: 64 };
                            case "htmlFile":
                                return { iconSrc: HtmlLine, iconColor: "warning", iconSize: 64 };
                            case "zipFile":
                                return { iconSrc: ZipLine, iconColor: "secondary", iconSize: 64 };
                            case "link":
                                return { iconSrc: Link2, iconColor: "primary", iconSize: 32 };
                            default:
                                return { iconSrc: Download, iconColor: "border", iconSize: 32 };
                        }
                    };
                    const { iconSrc, iconColor, iconSize } = getIconAndColor(variant);

                    return (
                        isLinkOrDocument && (
                            <GridItem
                                {...styles.tableGridRow}
                                key={index}
                            >
                                <GridContainer {...styles.tableGridRowContainer}>
                                    <GridItem {...styles.documentLinkTableGridCell}>
                                        <Link
                                            {...styles.titleLink}
                                            href={documentLinkHref}
                                            target="_new"
                                            aria-label={overrideTitle || documentLinkTitle}
                                        >
                                            {overrideTitle || documentLinkTitle}
                                        </Link>
                                    </GridItem>
                                    <GridItem {...styles.documentIconTableGridCell}>
                                        <Link
                                            {...styles.iconLink}
                                            href={documentLinkHref}
                                            target="_new"
                                        >
                                            <Icon
                                                src={iconSrc}
                                                color={iconColor}
                                                size={iconSize}
                                            />
                                        </Link>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        )
                    );
                })}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<StoreFieldDefinition> = {
    component: DocumentGrid,
    definition: {
        group: "Common",
        displayName: "Documents Grid",
        icon: "border-all",
        fieldDefinitions: [
            {
                name: fields.documentsLinks,
                editorTemplate: "ListField",
                defaultValue: [],
                isRequired: true,
                getDisplay: (item: HasFields) => {
                    const {
                        document,
                        overrideTitle,
                        variant,
                    } = item.fields;

                    if (overrideTitle) {
                        if (variant) {
                            return `${overrideTitle} (${variant})`;
                        }
                        return overrideTitle;
                    }

                    return document;
                },
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "isLinkOrDocument",
                        displayName: "Type",
                        editorTemplate: "DropDownField",
                        hideEmptyOption: true,
                        options: [
                            { displayName: "Document", value: "Document" },
                            { displayName: "Link", value: "Link" },
                        ],
                        defaultValue: "Document",
                    },
                    {
                        name: "document",
                        displayName: "Document",
                        editorTemplate: "FilePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.isLinkOrDocument === "Document",
                        isRequired: true,
                    },
                    {
                        name: "link",
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isVisible: widget => widget.fields.isLinkOrDocument === "Link",
                        isRequired: true,
                    },
                    {
                        name: "overrideTitle",
                        displayName: "Override Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "variant",
                        displayName: "File's type",
                        editorTemplate: "DropDownField",
                        tooltip: "Determines the icon to be displayed",
                        options: [
                            {
                                displayName: "Pdf document",
                                value: "pdfDocument",
                            },
                            {
                                displayName: "Docx document",
                                value: "docxDocument",
                            },
                            {
                                displayName: "Xlsx spreadsheet",
                                value: "xlsxSpreadsheet",
                            },
                            {
                                displayName: "Html file",
                                value: "htmlFile",
                            },
                            {
                                displayName: "Zip file",
                                value: "zipFile",
                            },
                            {
                                displayName: "Link",
                                value: "link",
                            },
                            {
                                displayName: "Other",
                                value: "other",
                            },
                        ],
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;